import { toast } from 'sonner';
import { Badge, Btn, Card, Overline } from '../../ForBrands/ui/BrandUI';
import '../../ForBrands/brandMode.css';

// Verification state card for a coach, in the V2 style. Rendered states:
//   pending  -> "Awaiting admin verification" (no code visible)
//   verified -> "You're verified" + the team code of the coach's primary
//               team with copy-to-clipboard (one code per team, shared by
//               every coach of the team)
//   rejected -> "Verification rejected" + contact prompt
//   (none)   -> hidden entirely (e.g. coach hasn't run onboarding yet)
//
// Lives in its own file so multiple pages (coach profile, team pages, any
// future coach-facing surface) can drop the same panel in.
const CoachVerificationPanel = ({ verification }) => {
  if (!verification || !verification.status) return null;
  const { status, verificationCode } = verification;
  const teamName = (verification.currentTeam || verification.teamName || '').trim();

  const copyCode = () => {
    if (!verificationCode) return;
    navigator.clipboard?.writeText(verificationCode).then(
      () => toast.success('Code copied. Share it with your athletes.'),
      () => toast.error('Could not copy. Select and copy manually.'),
    );
  };

  if (status === 'verified') {
    return (
      <Card padding="p-6" className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <Overline>Verification</Overline>
          <Badge tone="outline" dot="#1f9d55">
            Verified coach
          </Badge>
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="bm-h-s text-bm-ink">You're verified</h2>
          <p className="bm-body-m text-bm-muted">
            Athletes you coach enter the team code below during onboarding, or any time from their profile, to link to your roster.
          </p>
        </div>
        <div className="flex flex-col items-center gap-3 rounded-[12px] border border-bm-border bg-bm-subtle px-6 py-5 sm:flex-row sm:justify-between">
          <div className="flex flex-col items-center gap-1 sm:items-start">
            <Overline>Team code</Overline>
            <code className="select-all font-archivo text-[30px] font-bold leading-none tracking-[0.3em] text-bm-ink">{verificationCode}</code>
            {teamName && <span className="bm-body-s text-bm-muted">{`For ${teamName}. Every coach of the team shares it.`}</span>}
          </div>
          <Btn variant="secondary" size="sm" onClick={copyCode}>
            Copy code
          </Btn>
        </div>
      </Card>
    );
  }

  if (status === 'rejected') {
    return (
      <Card padding="p-6" className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <Overline>Verification</Overline>
          <Badge tone="critical">Rejected</Badge>
        </div>
        <h2 className="bm-h-s text-bm-ink">Verification rejected</h2>
        <p className="bm-body-m text-bm-muted">
          Your coach verification request was rejected. Email{' '}
          <a href="mailto:hello@cachescore.com" className="text-bm-orange underline underline-offset-2">
            hello@cachescore.com
          </a>{' '}
          to discuss next steps.
        </p>
      </Card>
    );
  }

  // Default: pending
  return (
    <Card padding="p-6" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <Overline>Verification</Overline>
        <Badge tone="attention">Pending review</Badge>
      </div>
      <h2 className="bm-h-s text-bm-ink">Awaiting admin verification</h2>
      <p className="bm-body-m text-bm-muted">
        We received your team selection. An admin reviews and verifies your role within 1 business day. Once approved, a team code appears here for
        your athletes to enter, which links them to your roster.
      </p>
      <p className="bm-body-s text-bm-tertiary">CacheShare™ and the team pages stay locked until verification completes. You can edit your profile and check back any time.</p>
    </Card>
  );
};

export default CoachVerificationPanel;
